import { useState, useEffect } from "react";
import { ShieldCheck } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import api from "../../lib/api";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES"); } catch { return iso; }
};

/**
 * Shows how many duplicates the system has prevented in the last 30 days,
 * along with the total amount the restaurant would have double-counted
 * in the P&L. Hides itself when there's nothing to brag about yet.
 */
export default function DuplicatesPreventedWidget({ restaurantId, refreshKey = 0 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    api.duplicatesPreventedSummary(restaurantId, 30)
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { /* silent — widget is opt-in */ });
    return () => { cancelled = true; };
  }, [restaurantId, refreshKey]);

  if (!data || data.count === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="duplicates-prevented-widget"
          className="w-full flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-left hover:bg-emerald-100 transition"
        >
          <div className="shrink-0 h-10 w-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-emerald-900">
              Hemos evitado {data.count} duplicado{data.count === 1 ? "" : "s"} este mes
            </div>
            <div className="text-xs text-emerald-800">
              Ahorro detectado: <strong>{fmtEUR(data.total_amount)}</strong> que no se duplicarán en tu P&amp;L.
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">
            Ver detalle
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="px-4 py-3 border-b">
          <h4 className="text-sm font-semibold">Últimos duplicados evitados</h4>
          <p className="text-[11px] text-muted-foreground">
            En los últimos {data.window_days} días
          </p>
        </div>
        <ul className="max-h-[280px] overflow-auto divide-y">
          {(data.recent || []).map((r, i) => (
            <li key={i} className="px-4 py-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">
                  {r.supplier_name || (r.kind === "expense" ? "Gasto manual" : "Factura")}
                </span>
                <span className="text-emerald-700 font-semibold">
                  {fmtEUR(r.amount)}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                <span className="truncate italic">{r.reason || ""}</span>
                <span className="shrink-0">{fmtDate(r.prevented_at)}</span>
              </div>
            </li>
          ))}
          {(!data.recent || data.recent.length === 0) && (
            <li className="px-4 py-3 text-xs text-muted-foreground text-center">
              Sin eventos recientes.
            </li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
